/**
 * @copyright Copyright 2016 The MathWorks, Inc.
 */
define([
    "dojo/dom-geometry",
    "dojo/on",
    "dojo/window",
    "./stopEvent"
], function (domGeometry, on, win, stopEvent) {

    let getDialogPositionAfterMoveWithSpecifiedPositionSet =
        (dialog, specifiedPosition, dialogDimension, windowDimension, bindingNodeDimension) => {
            let dialogPosition = {};

            if (bindingNodeDimension) {
                dialogPosition.l = Math.max(bindingNodeDimension.x,
                    Math.min(bindingNodeDimension.x + specifiedPosition.x,
                        bindingNodeDimension.x + bindingNodeDimension.w - dialogDimension.w));
                dialogPosition.t = Math.max(bindingNodeDimension.y,
                    Math.min(bindingNodeDimension.y + specifiedPosition.y,
                        bindingNodeDimension.y + bindingNodeDimension.h - dialogDimension.h));
                dialog._specifiedPosition = {
                    x: dialogPosition.l - bindingNodeDimension.x,
                    y: dialogPosition.t - bindingNodeDimension.y
                };
            } else {
                dialogPosition.l = Math.max(0,
                    Math.min(specifiedPosition.x, windowDimension.w - dialogDimension.w));
                dialogPosition.t = Math.max(0,
                    Math.min(specifiedPosition.y, windowDimension.h - dialogDimension.h));
                dialog._specifiedPosition = {
                    x: dialogPosition.l,
                    y: dialogPosition.t
                };
            }

            dialog._isCenter = false;

            return dialogPosition;
        };

    let getDialogPositionAfterMoveWithoutSpecifiedPositionSet =
        (dialog, specifiedPosition, dialogDimension, windowDimension, bindingNodeDimension) => {
            let dialogPosition = {};

            if (bindingNodeDimension) {
                // If dialog width is larger than the bindingNode width or the bindingNode width in
                // the viewport, set it to the most left of the bindingNode or viewport;
                // Otherwise, set it to the horizontal middle of the bindingNode
                if (dialogDimension.w >= bindingNodeDimension.x + bindingNodeDimension.w
                    || dialogDimension.w >= windowDimension.w - bindingNodeDimension.x
                    || dialogDimension.w >= bindingNodeDimension.w) {
                    dialogPosition.l = Math.max(0, bindingNodeDimension.x);
                } else {
                    dialogPosition.l = (bindingNodeDimension.x >= 0 ? bindingNodeDimension.x : 0)
                        + (bindingNodeDimension.x + bindingNodeDimension.w <= windowDimension.w ?
                        bindingNodeDimension.w / 2 :
                        (windowDimension.w - bindingNodeDimension.x) / 2) - dialogDimension.w / 2;
                }

                // If dialog height is larger than the bindingNode height or the bindingNode height
                // in the viewport, set it to the most top of the bindingNode or viewport;
                // Otherwise, set it to the vertical middle of the bindingNode
                if (dialogDimension.h >= bindingNodeDimension.y + bindingNodeDimension.h
                    || dialogDimension.h >= windowDimension.h - bindingNodeDimension.y
                    || dialogDimension.h >= bindingNodeDimension.h) {
                    dialogPosition.t = Math.max(0, bindingNodeDimension.y);
                } else {
                    dialogPosition.t = (bindingNodeDimension.y >= 0 ? bindingNodeDimension.y : 0)
                        + (bindingNodeDimension.y + bindingNodeDimension.h <= windowDimension.h ?
                        bindingNodeDimension.h / 2 :
                        (windowDimension.h - bindingNodeDimension.y) / 2) - dialogDimension.h / 2;
                }
            } else {
                // Set the dialog to the center of the viewport unless viewport is smaller than the
                // dialog size. In the later situation, set dialog to the most left or top
                dialogPosition.l = Math.max(0, windowDimension.w / 2 - dialogDimension.w / 2);
                dialogPosition.t = Math.max(0, windowDimension.h / 2 - dialogDimension.h / 2);
            }

            dialog._isCenter = true;

            return dialogPosition;
        };

    let getDialogPositionAfterBrowserScroll = (dialog, dialogDimension, scrollLeft, lastScrollLeft,
                                               scrollTop, lastScrollTop, bindingNodeDimension) => {
        let dialogPosition = {};

        if (bindingNodeDimension) {
            dialogPosition.l = Math.max(bindingNodeDimension.x,
                Math.min(dialogDimension.x - scrollLeft + lastScrollLeft,
                    bindingNodeDimension.x + bindingNodeDimension.w - dialogDimension.w));
            dialogPosition.t = Math.max(bindingNodeDimension.y,
                Math.min(dialogDimension.y - scrollTop + lastScrollTop,
                    bindingNodeDimension.y + bindingNodeDimension.h - dialogDimension.h));
        } else {
            dialogPosition.l = dialogDimension.x;
            dialogPosition.t = dialogDimension.y;
        }

        return dialogPosition;
    };

    let getDialogPositionAfterBrowserResize =
        (dialog, dialogDimension, windowDimension, bindingNodeDimension) => {
            let dialogPosition = {};

            if (bindingNodeDimension) {
                if (dialog._isCenter) {
                    dialogPosition.l = Math.max(0, Math.max(bindingNodeDimension.x,
                        Math.min(windowDimension.w - dialogDimension.w,
                            Math.min(bindingNodeDimension.x + bindingNodeDimension.w / 2 -
                                dialogDimension.w / 2,
                                bindingNodeDimension.x + bindingNodeDimension.w -
                                dialogDimension.w))));
                    dialogPosition.t = Math.max(0, Math.max(bindingNodeDimension.y,
                        Math.min(windowDimension.h - dialogDimension.h,
                            Math.min(bindingNodeDimension.y + bindingNodeDimension.h / 2 -
                                dialogDimension.h / 2,
                                bindingNodeDimension.y + bindingNodeDimension.h -
                                dialogDimension.h))));
                } else {
                    dialogPosition.l = Math.max(0, Math.max(bindingNodeDimension.x,
                        Math.min(windowDimension.w - dialogDimension.w,
                            Math.min(dialog._specifiedPosition.x + bindingNodeDimension.x,
                                bindingNodeDimension.x + bindingNodeDimension.w -
                                dialogDimension.w))));
                    dialogPosition.t = Math.max(0, Math.max(bindingNodeDimension.y,
                        Math.min(windowDimension.h - dialogDimension.h,
                            Math.min(dialog._specifiedPosition.y + bindingNodeDimension.y,
                                bindingNodeDimension.y + bindingNodeDimension.h -
                                dialogDimension.h))));
                }
            } else {
                if (dialog._isCenter) {
                    dialogPosition.l = Math.max(0,
                        Math.min(windowDimension.w / 2 - dialogDimension.w / 2,
                            windowDimension.w - dialogDimension.w));
                    dialogPosition.t = Math.max(0,
                        Math.min(windowDimension.h / 2 - dialogDimension.h / 2,
                            windowDimension.h - dialogDimension.h));
                } else {
                    dialogPosition.l = Math.max(0, Math.min(dialog._specifiedPosition.x,
                        windowDimension.w - dialogDimension.w));
                    dialogPosition.t = Math.max(0, Math.min(dialog._specifiedPosition.y,
                        windowDimension.h - dialogDimension.h));
                }
            }

            return dialogPosition;
        };

    /**
     * DialogController provides control APIs for dialogs
     * @module mw-dialogs/controller/DialogController
     */
    return {

        /**
         * Places the dialog at center or an xy based position from left and top.
         * If position is not specified, place the dialog into the center of the
         * window or constraint
         *
         * @static
         *
         * @param {Object} [dialog] - The dialog instance
         * @param {Object} [position] - An object containing the x and y values of the location to
         * specifies where the dialog should be displayed. E.g., {x: 0, y: 0}
         * If this property is not specified, then dialog will position at the center of the window
         * or constraint.
         */
        moveTo (dialog, position) {
            let dialogDimension = domGeometry.position(dialog.domNode);
            let dialogPosition = {};
            let windowDimension = win.getBox();
            if (typeof dialog._bindingNode === 'object') {
                let bindingNodeDimension = domGeometry.position(dialog._bindingNode);
                if (position) {
                    dialogPosition = getDialogPositionAfterMoveWithSpecifiedPositionSet(dialog,
                        position, dialogDimension, windowDimension, bindingNodeDimension);
                } else {
                    dialogPosition = getDialogPositionAfterMoveWithoutSpecifiedPositionSet(dialog,
                        position, dialogDimension, windowDimension, bindingNodeDimension);
                }
            } else {
                if (position) {
                    dialogPosition = getDialogPositionAfterMoveWithSpecifiedPositionSet(dialog,
                        position, dialogDimension, windowDimension);
                } else {
                    dialogPosition = getDialogPositionAfterMoveWithoutSpecifiedPositionSet(dialog,
                        position, dialogDimension, windowDimension);
                }
            }

            dialog.domNode.style.left = `${dialogPosition.l}px`;
            dialog.domNode.style.top = `${dialogPosition.t}px`;
        },

        /**
         * @desc Changes the height and width of the dialog, without a change in dialog position
         *
         * @static
         * @param {Object} dialog - The dialog instance
         * @param {Object} options - An object containing properties affecting resizeTo behavior
         * @param {Number} [options.height] - Y-length in pixels
         * @param {Number} [options.width] - X-length in pixels
         *
         * @example dialog.resizeTo({height: 600, width: 760})
         *
         */
        resizeTo (dialog, options) {
            if (dialog.domNode) {
                let heightUnits = options.heightUnits || "px";
                let widthUnits = options.widthUnits || "px";

                if (this._isValidUnit(heightUnits) && this._isValidUnit(widthUnits)) {
                    if (options.height) {
                        dialog.domNode.style.height = `${options.height}${heightUnits}`;
                    }
                    if (options.width) {
                        dialog.domNode.style.width = `${options.width}${widthUnits}`;
                    }
                }
            }
        },

        /**
         * Handle dialog position when viewport scrolls
         */
        addScrollHandler (dialog) {
            let lastScrollTop = window.pageYOffset;
            let lastScrollLeft = window.pageXOffset;

            // Fix the bug that on mobile safari, scroll will make dialog fly away
            // TODO: will find better solution when we plan to support touch events
            /*dialog._trackEventListener(on(document, 'touchmove', (e) => {
                stopEvent(e);
            }));*/

            dialog._trackEventListener(on(document, 'scroll', () => {
                let st = window.pageYOffset;
                let sl = window.pageXOffset;
                let dialogDimension = domGeometry.position(dialog.domNode);
                let dialogPosition = {};

                if (typeof dialog._bindingNode === 'object') {
                    let bindingNodeDimension = domGeometry.position(dialog._bindingNode);
                    dialogPosition = getDialogPositionAfterBrowserScroll(dialog, dialogDimension,
                        sl, lastScrollLeft, st, lastScrollTop, bindingNodeDimension);
                } else {
                    dialogPosition = getDialogPositionAfterBrowserScroll(dialog, dialogDimension,
                        sl, lastScrollLeft, st, lastScrollTop);
                }

                dialog.domNode.style.left = `${dialogPosition.l}px`;
                dialog.domNode.style.top = `${dialogPosition.t}px`;

                lastScrollTop = st;
                lastScrollLeft = sl;
            }));
        },

        /**
         * Handle dialog position when viewport resizes
         */
        addBrowserResizeHandler (dialog) {

            dialog._trackEventListener(on(window, 'resize', () => {

                let dialogDimension = domGeometry.position(dialog.domNode);
                let windowDimension = win.getBox();
                let dialogPosition = {};

                if (typeof dialog._bindingNode === 'object') {
                    let bindingNodeDimension = domGeometry.position(dialog._bindingNode);
                    dialogPosition = getDialogPositionAfterBrowserResize(dialog, dialogDimension,
                        windowDimension, bindingNodeDimension);
                } else {
                    dialogPosition = getDialogPositionAfterBrowserResize(dialog, dialogDimension,
                        windowDimension);
                }

                dialog.domNode.style.left = `${dialogPosition.l}px`;
                dialog.domNode.style.top = `${dialogPosition.t}px`;

            }));
        },

        /**
         * Handle text selection within the dialog content area bounds
         */
        selectionWithinDialogBounds (dialog) {
            let isSelectReady;
            dialog._trackEventListener(on(document, 'mousedown', (e) => {
                if (e.target === dialog._contentWidget.domNode) {
                    isSelectReady = true;
                }
            }));

            dialog._trackEventListener(on(document, 'mouseup', () => {
                isSelectReady = false;
            }));

            dialog._trackEventListener(on(document, 'mousemove', (e) => {
                if (isSelectReady) {
                    if (e.target !== dialog._contentWidget.domNode) {
                        stopEvent(e);
                    }
                }
            }));
        },

        /**
         * Checks if unit is valid for use
         *
         * @static
         *
         * @param {String) unitVal - the units as string
         * @returns {boolean} - true if the units are allowed, false otherwise
         * @private
         */
        _isValidUnit (unitVal) {
            let validUnits = ['px'];
            return validUnits.some((x) => {
                return x === unitVal;
            });
        }
    };
});
